/**
 * Toast Notifications
 */

/**
 * Toast notification types
 */
export type ToastType = 'info' | 'success' | 'error' | 'warning';

/**
 * Toast stacking offset in pixels
 */
const TOAST_STACK_OFFSET = 80; // Approximate height of one toast + gap

/**
 * Active toasts tracking for queue management
 */
const activeToasts: HTMLElement[] = [];

/**
 * Show a toast notification
 */
export function showToast(message: string, type: ToastType = 'info', duration: number = 3000): HTMLElement {
  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  
  // Stack toasts vertically if multiple are active
  const offset = activeToasts.length * TOAST_STACK_OFFSET;
  if (offset > 0) {
    toast.style.bottom = `calc(2rem + ${offset}px)`;
  }
  
  document.body.appendChild(toast);
  activeToasts.push(toast);
  
  // Trigger animation
  requestAnimationFrame(() => {
    toast.classList.add('toast--visible');
  });
  
  // Remove after duration
  setTimeout(() => {
    dismissToast(toast);
  }, duration);
  
  return toast;
}

/**
 * Dismiss a specific toast
 */
export function dismissToast(toast: HTMLElement): void {
  if (!toast.parentNode) return;
  
  toast.classList.remove('toast--visible');
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
    // Remove from active toasts array
    const index = activeToasts.indexOf(toast);
    if (index > -1) {
      activeToasts.splice(index, 1);
    }
  }, 300); // Match CSS transition duration
}

/**
 * Dismiss all active toasts
 */
export function dismissAllToasts(): void {
  const toastsCopy = [...activeToasts];
  toastsCopy.forEach(toast => dismissToast(toast));
}

/**
 * Show a success toast
 */
export function success(message: string, duration: number = 3000): void {
  showToast(message, 'success', duration);
}

/**
 * Show an error toast
 */
export function error(message: string, duration: number = 5000): void {
  showToast(message, 'error', duration);
}

/**
 * Show a warning toast
 */
export function warning(message: string, duration: number = 4000): void {
  showToast(message, 'warning', duration);
}

/**
 * Show an info toast
 */
export function info(message: string, duration: number = 3000): void {
  showToast(message, 'info', duration);
}

export default {
  showToast,
  success,
  error,
  warning,
  info,
  dismissToast,
  dismissAllToasts
};
